import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ClipboardList } from 'lucide-react';
import { formsApi, projectsApi, type FormResponseSummary, type MyFormsResult } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useOpenForm } from '@/components/forms/FormListRow';

/** A form the viewer still has to fill on this project, and the response to resume (null = start one). */
interface FormToFill {
  formId: string;
  title: string;
  existing: FormResponseSummary | null;
  dueDate: string | null;
}

/**
 * What the viewer has to fill on this project (Jesse, 2026-11-04): forms
 * assigned to them or already started — for everyone, staff included. Clients
 * also get open forms they've never submitted; once submitted it drops off
 * (open forms are repeatable, not owed). The team wrote the open forms, so
 * they'd only be noise on every overview.
 */
function formsToFill(myForms: MyFormsResult | undefined, projectId: string, includeUnstartedOpen: boolean): FormToFill[] {
  if (!myForms) return [];
  const started = myForms.assigned
    .filter((r) => r.form.projectId === projectId)
    .map((r) => ({ formId: r.formId, title: r.form.title, existing: r as FormResponseSummary, dueDate: r.dueDate ?? null }));
  if (!includeUnstartedOpen) return started;
  const startedIds = new Set(started.map((f) => f.formId));
  const open = myForms.open
    .filter((f) => f.projectId === projectId && !startedIds.has(f.id) && !f.myDraft && f.mySubmittedCount === 0)
    .map((f) => ({ formId: f.id, title: f.title, existing: null, dueDate: null }));
  return [...started, ...open];
}

/**
 * Always the date, never "3 days overdue": nothing on the overview puts a
 * client in a bad light. Due dates are calendar days stored at UTC midnight,
 * so read them in UTC or the Americas see the day before.
 */
function dueText(dueDate: string) {
  return `Due ${new Date(dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
}

/**
 * The overview's call to action for forms the viewer owes on this project
 * (Jesse, 2026-11-04: "more like an alert that gets them to fill it out",
 * not a line inside At a glance). Sits above the grid, renders nothing when
 * there's nothing to fill, and can't be dismissed: the form is still owed.
 */
export function FormsToFillBanner({ projectId }: { projectId: string }) {
  // Same queries as At a glance and the Forms page, so the cache is shared and
  // the three never disagree about what's outstanding.
  const { data: stats } = useQuery({
    queryKey: ['project-stats', projectId],
    queryFn: () => projectsApi.stats(projectId),
    staleTime: 60_000,
  });
  const { data: myForms } = useQuery({
    queryKey: ['myForms'],
    queryFn: () => formsApi.my(),
    staleTime: 30_000,
  });
  const { open, startingId } = useOpenForm();

  const forms = formsToFill(myForms, projectId, stats?.perspective === 'client');
  if (forms.length === 0) return null;

  const heading = forms.length === 1 ? 'You have a form to fill out' : `You have ${forms.length} forms to fill out`;

  return (
    <section aria-label="Forms to fill out" className="rounded-xl border border-accent/30 bg-accent/5 p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10">
          <ClipboardList className="h-5 w-5 text-accent" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-secondary">{heading}</h2>
          <ul className="mt-2 divide-y divide-accent/15">
            {forms.map((f) => {
              const inProgress = !!f.existing && f.existing.status !== 'pending';
              return (
                <li key={f.formId} className="flex flex-col gap-2 py-2.5 first:pt-1 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-secondary">{f.title}</p>
                    {(inProgress || f.dueDate) && (
                      <p className="mt-0.5 text-xs text-grey-500">
                        {[inProgress && 'In progress', f.dueDate && dueText(f.dueDate)].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    className="shrink-0 self-start sm:self-auto"
                    onClick={() => open(f.formId, f.existing)}
                    isLoading={startingId === f.formId}
                  >
                    {inProgress ? 'Continue' : 'Fill out'}
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden />
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
